/**
 * Settings button that opens the settings modal
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useSettingsStore } from '../../stores/settingsStore';

export function SettingsButton() {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Return focus to trigger when modal closes
  const handleClose = useCallback(() => {
    setIsOpen(false);
    // Use requestAnimationFrame to ensure focus returns after modal unmounts
    requestAnimationFrame(() => triggerRef.current?.focus());
  }, []);

  // Focus trap and keyboard handling
  useEffect(() => {
    if (!isOpen) return;

    // Focus the close button when modal opens
    closeButtonRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Mark handled so the pause shortcut doesn't also fire
        e.preventDefault();
        handleClose();
        return;
      }

      // Focus trap
      if (e.key === 'Tab' && modalRef.current) {
        const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleClose]);

  const {
    colorblindMode,
    reducedMotion,
    highContrast,
    soundEffects,
    hapticFeedback,
    toggleColorblindMode,
    toggleReducedMotion,
    toggleHighContrast,
    toggleSoundEffects,
    toggleHapticFeedback,
  } = useSettingsStore(
    useShallow((s) => ({
      colorblindMode: s.colorblindMode,
      reducedMotion: s.reducedMotion,
      highContrast: s.highContrast,
      soundEffects: s.soundEffects,
      hapticFeedback: s.hapticFeedback,
      toggleColorblindMode: s.toggleColorblindMode,
      toggleReducedMotion: s.toggleReducedMotion,
      toggleHighContrast: s.toggleHighContrast,
      toggleSoundEffects: s.toggleSoundEffects,
      toggleHapticFeedback: s.toggleHapticFeedback,
    }))
  );

  return (
    <>
      <button
        ref={triggerRef}
        onClick={() => setIsOpen(true)}
        className="p-1.5 sm:p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
        aria-label="Settings"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <svg
          className="w-5 h-5 text-slate-600 dark:text-slate-300"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      </button>

      {isOpen && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4 safe-area-all"
          onClick={handleClose}
        >
          <div
            ref={modalRef}
            className="bg-white dark:bg-slate-800 rounded-xl p-5 sm:p-6 w-full max-w-sm sm:max-w-md shadow-xl max-h-full overflow-y-auto overscroll-contain"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="settings-title"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 id="settings-title" className="text-xl font-bold text-slate-900 dark:text-white">Settings</h2>
              <button
                ref={closeButtonRef}
                onClick={handleClose}
                className="touch-target flex items-center justify-center p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                aria-label="Close settings"
              >
                <svg className="w-5 h-5 text-slate-500 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase">Accessibility</h3>

              <SettingToggle
                label="Colorblind Mode"
                description="Use patterns and symbols to distinguish vowels/consonants"
                checked={colorblindMode}
                onChange={toggleColorblindMode}
              />

              <SettingToggle
                label="High Contrast"
                description="Stronger tile borders and text contrast"
                checked={highContrast}
                onChange={toggleHighContrast}
              />

              <SettingToggle
                label="Reduced Motion"
                description="Minimize animations for motion sensitivity"
                checked={reducedMotion}
                onChange={toggleReducedMotion}
              />

              <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase pt-4">Preferences</h3>

              <SettingToggle
                label="Sound Effects"
                description="Play sounds for game events"
                checked={soundEffects}
                onChange={toggleSoundEffects}
              />

              <SettingToggle
                label="Haptic Feedback"
                description="Vibrate on actions (mobile only)"
                checked={hapticFeedback}
                onChange={toggleHapticFeedback}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function SettingToggle({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="font-medium text-slate-900 dark:text-white">{label}</div>
        <div className="text-sm text-slate-500 dark:text-slate-400">{description}</div>
      </div>
      <button
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={onChange}
        className={`
          relative shrink-0 w-11 h-6 rounded-full transition-colors
          ${checked ? 'bg-blue-500' : 'bg-slate-300 dark:bg-slate-600'}
        `}
      >
        <span
          className={`
            absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform
            ${checked ? 'translate-x-5' : 'translate-x-0'}
          `}
        />
      </button>
    </div>
  );
}
